import { useEffect, useState } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";

import { useForgotPasswordMutation } from "@/api/auth/useAuthAPI";
import LoginPageLayout from "@/components/layout/login";
import ForgotPasswordForm from "@/components/login/ForgotPasswordForm";
import LoginForm from "@/components/login/LoginForm";
import LoadingPage from "@/components/ui/LoadingPage";
import { useAuth } from "@/hooks/auth/useAuth";
import { LoginPageType } from "@/types/auth";

const LoginPage = (): JSX.Element => {
  const { isAuthenticating, login, isAuthenticated, isLoggingIn } = useAuth();

  const navigate = useNavigate();
  const location: Location<{ from?: string }> = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/";

  const [mutateCount, setMutateCount] = useState(0);
  const {
    mutate: forgotPassword,
    status: forgotPasswordStatus,
    reset: resetForgotPassword
  } = useForgotPasswordMutation({
    onSuccess: () => {
      setMutateCount((prev) => prev + 1);
    },
    onError: () => {
      setMutateCount((prev) => prev + 1);
    }
  });

  // Can be changed whether want to use content of login / forgot password page
  const [pageType, setPageType] = useState<LoginPageType>("login");

  // Effect to redirect to previous path (or root) if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [navigate, isAuthenticated, from]);

  if (isAuthenticated || isAuthenticating || isLoggingIn) {
    return <LoadingPage />;
  }

  // Render the login page when user is not authenticated
  return (
    <LoginPageLayout>
      <div className="flex flex-col flex-1 mt-[278px] lg:mt-[278px] xl:mt-[209px] 2xl:mt-[394px] mx-auto w-full">
        {pageType === "login" && (
          <LoginForm
            setPageType={setPageType}
            isLoggingIn={isLoggingIn}
            onSubmit={login}
          />
        )}
        {pageType === "resetPassword" && (
          <ForgotPasswordForm
            setPageType={setPageType}
            forgotPasswordStatus={forgotPasswordStatus}
            mutateCount={mutateCount}
            onSubmit={forgotPassword}
            onReset={() => {
              setMutateCount(0);
              resetForgotPassword();
            }}
          />
        )}
      </div>
    </LoginPageLayout>
  );
};

export default LoginPage;
